import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bookmark,
  CancelSchedule,
  Check,
  ChevronDown,
  Delete,
  Report,
  Schedule,
  ScheduleEdit
} from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { RemoveSavedReportModal } from "@/components/global/Chat/components/dreamerReports/RemoveSavedReportModal";
import RemoveScheduleConfirmationModal from "@/components/global/Chat/components/ScheduleModal/RemoveScheduleConfirmationModal";
import { buildScheduledForText } from "@/components/global/Chat/components/ScheduleModal/buildScheduleSummary";
import { ScheduleData } from "@/components/global/Chat/components/ScheduleModal/types";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

const SAVE_TRANSLATION_KEY = `${TRANSLATION_KEY}.save`;

// The saved menu is split into its schedule, manage and remove groups, with a
// divider between each.
const SCHEDULE_SECTION = "schedule";
const MANAGE_SECTION = "manage";
const REMOVE_SECTION = "remove";

const menuIcon = (icon: JSX.Element, destructive = false) => (
  <IconContainer size="5" color={destructive ? "negative" : "secondary"}>
    {icon}
  </IconContainer>
);

const chevron = (
  <IconContainer
    size="sm"
    color="current"
    className="transition-transform duration-200 group-data-[state=open]:rotate-180"
  >
    <ChevronDown />
  </IconContainer>
);

interface ReportSaveControlProps {
  isSaved: boolean;
  isScheduled: boolean;
  /** The report's cadence, which the scheduled control describes. */
  scheduleConfig?: ScheduleData | null;
  reportName: string;
  isSaving?: boolean;
  /** Which trigger edge the menu lines up with; a modal header opens it leftwards. */
  align?: "start" | "end";
  /** The expanded view of the report, which has no Manage reports item. */
  inReportView?: boolean;
  onSave: () => void;
  onSaveAndSchedule: () => void;
  onSchedule: () => void;
  onManageReports: () => void;
  onRemoveSchedule: () => void;
  onRemove: () => void;
}

/**
 * The Save control under a report Dreamer returned in chat. Before the report
 * is saved it offers saving with or without a schedule; after, it reads Saved —
 * or Scheduled, once it has a cadence — and manages the report from where the
 * user already is.
 */
export const ReportSaveControl: React.FC<ReportSaveControlProps> = ({
  isSaved,
  isScheduled,
  scheduleConfig = null,
  reportName,
  isSaving = false,
  align = "start",
  inReportView = false,
  onSave,
  onSaveAndSchedule,
  onSchedule,
  onManageReports,
  onRemoveSchedule,
  onRemove
}) => {
  const { t } = useTranslation();
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [isRemoveScheduleConfirmOpen, setIsRemoveScheduleConfirmOpen] =
    useState(false);

  const unsavedOptions: DetailedViewOptions = {
    saveReport: {
      action: onSave,
      customText: t(`${SAVE_TRANSLATION_KEY}.saveReport`),
      customIcon: menuIcon(<Bookmark />)
    },
    saveAndSchedule: {
      action: onSaveAndSchedule,
      customText: t(`${SAVE_TRANSLATION_KEY}.saveAndSchedule`),
      customIcon: menuIcon(<Schedule />)
    }
  };

  const scheduleOptions: DetailedViewOptions = isScheduled
    ? {
        editSchedule: {
          action: onSchedule,
          customText: t(`${SAVE_TRANSLATION_KEY}.editSchedule`),
          customIcon: menuIcon(<ScheduleEdit />),
          section: SCHEDULE_SECTION
        },
        removeSchedule: {
          action: () => setIsRemoveScheduleConfirmOpen(true),
          customText: t(
            `${SAVE_TRANSLATION_KEY}.${inReportView ? "removeScheduleInView" : "removeSchedule"}`
          ),
          customIcon: menuIcon(<CancelSchedule />),
          section: SCHEDULE_SECTION
        }
      }
    : {
        scheduleReport: {
          action: onSchedule,
          customText: t(`${SAVE_TRANSLATION_KEY}.scheduleReport`),
          customIcon: menuIcon(<Schedule />),
          section: SCHEDULE_SECTION
        }
      };

  const savedOptions: DetailedViewOptions = {
    ...scheduleOptions,
    ...(!inReportView && {
      manageReports: {
        action: onManageReports,
        customText: t(`${SAVE_TRANSLATION_KEY}.manageReports`),
        customIcon: menuIcon(<Report />),
        section: MANAGE_SECTION
      }
    }),
    removeFromSaved: {
      action: () => setIsRemoveConfirmOpen(true),
      customText: t(`${SAVE_TRANSLATION_KEY}.removeFromSaved`),
      customIcon: menuIcon(<Delete />, true),
      variant: "destructive",
      section: REMOVE_SECTION
    }
  };

  // A scheduled report says what its cadence is, over the menu and on hover.
  const scheduledForText =
    isSaved && isScheduled && scheduleConfig
      ? buildScheduledForText(t, scheduleConfig)
      : undefined;
  const savedSections = [
    {
      id: SCHEDULE_SECTION,
      title: scheduledForText,
      // A long cadence wraps rather than widening the whole menu.
      titleClassName: "max-w-[420px]"
    },
    // Left without items in the report view, where the menu draws no group for it.
    { id: MANAGE_SECTION },
    { id: REMOVE_SECTION }
  ];

  const trigger = isSaved ? (
    <span
      tabIndex={isSaving ? -1 : 0}
      // The shared menu's `disabled` reaches only its own trigger, so a write
      // still in flight is guarded here — a repeated removal would 404.
      aria-disabled={isSaving}
      className={cn(
        "group inline-flex size-fit",
        isSaving && "pointer-events-none opacity-50"
      )}
      aria-label={t(
        `${SAVE_TRANSLATION_KEY}.${isScheduled ? "scheduledAriaLabel" : "savedAriaLabel"}`
      )}
      data-test={isScheduled ? "report-scheduled-badge" : "report-saved-badge"}
    >
      <Badge className="border-blue-700 h-8 bg-[rgba(var(--surface-chips-natural-rgb),0.2)] cursor-pointer hover:shadow-[inset_0_0_0_9999px_var(--white-8)] group-data-[state=open]:shadow-[inset_0_0_0_9999px_var(--white-8)]">
        <IconContainer className="size-[18px]">
          <Check />
        </IconContainer>
        {t(`${SAVE_TRANSLATION_KEY}.${isScheduled ? "scheduled" : "saved"}`)}
        {chevron}
      </Badge>
    </span>
  ) : (
    <Button
      variant="secondary"
      size="lg"
      className="group h-8"
      disabled={isSaving}
      aria-label={t(`${SAVE_TRANSLATION_KEY}.saveAriaLabel`)}
      data-test="report-save-button"
    >
      <IconContainer size="sm">
        <Bookmark />
      </IconContainer>
      {t(`${SAVE_TRANSLATION_KEY}.save`)}
      {chevron}
    </Button>
  );

  return (
    <>
      <OptionsDropdownMenu
        tPrefix={SAVE_TRANSLATION_KEY}
        options={isSaved ? savedOptions : unsavedOptions}
        enableSections={isSaved}
        sections={savedSections}
        align={align}
        disabled={isSaving}
        disableCloseAutoFocus
        contentClassName="min-w-[200px]"
        customTrigger={trigger}
        tooltipText={scheduledForText}
      />
      <RemoveSavedReportModal
        isOpen={isRemoveConfirmOpen}
        setIsOpen={setIsRemoveConfirmOpen}
        reportName={reportName}
        isScheduled={isScheduled}
        onConfirm={() => {
          setIsRemoveConfirmOpen(false);
          onRemove();
        }}
      />
      <RemoveScheduleConfirmationModal
        isOpen={isRemoveScheduleConfirmOpen}
        setIsOpen={setIsRemoveScheduleConfirmOpen}
        onConfirm={onRemoveSchedule}
      />
    </>
  );
};
